/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/bundle';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PageSection from '../../organisms/page-section';
import { Card } from '../../molecules/course-card';
import { Head } from '../../../templates/head';
import { getBreadcrumLocale, pageTypes } from '../../../../utils/url-helpers';
import {
	getBreadcrumbsSeoSchema,
	getImageSeoSchema,
	getOrganizationSeoSchema,
} from '../../../../utils/seo-schema';
import { NewGluCardContainer } from '../../organisms/new-glu-course-container';
import { GluNewMainHeader } from '../../../molecules/new-glu-main-header';
import GLUExitPopUp from '../../../molecules/glu-exit-pop-up';

const NewGluClientPage = ({ data, location }) => {
	const {
		eLearningCoursesContent,
		courses: courseLinks,
		locale,
		slug,
	} = data.datoCmsGluClientPage;

	const [currentIndex, setCurrentIndex] = useState(0);

	const handleNext = () => {
		if (currentIndex < courseLinks.length - 4) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	const handlePrev = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	// eslint-disable-next-line no-unused-vars
	const courseData = data.allDatoCmsGluCourse;
	const { mainLogo } = data.allDatoCmsGluLogo.nodes[0];
	const { mainLogo: imageLogo } = slug.includes('are-you-ready')
		? data.allDatoCmsGluLogo.nodes[1]
		: data.allDatoCmsGluLogo.nodes[0];

	const { nodes: allGluCoursePages } = data.allDatoCmsGluCourseLink;
	const { nodes: id } = data.allCourses;
	const allCourseArray = id.flatMap((e) => e.id);

	const headerCTAContent = {
		...data.datoCmsGluClientPage.hero,
		header:
			data.datoCmsGluClientPage?.hero?.header?.value?.document
				?.children || [],
	};

	const metaContent = {
		noIndex: data.datoCmsGluClientPage.noIndex,
		noFollow: data.datoCmsGluClientPage.noFollow,
		metaInformation: data.datoCmsGluClientPage.seo,
		fallbackTitle: data.datoCmsGluClientPage.eLearningTitle,
		fallbackDescription: '',
		canonicalUrl: data.datoCmsGluClientPage?.canonicalUrl || location?.href,
	};

	const seoSchemas = {
		organization: getOrganizationSeoSchema({
			data,
			url: `${location?.origin}/${getBreadcrumLocale(location)}`,
		}),
		breadcrumbs: getBreadcrumbsSeoSchema({
			data,
			pageType: pageTypes.gluClientPage,
			location,
		}),
		image: getImageSeoSchema(data?.hero?.primaryImage?.url),
	};

	const onDemandCourses = data?.allDatoCmsGluCourse;
	const categoryIds = data.allCourseCategories.nodes;

	useMemo(() => {
		const allCourseCategories = [];
		const allCourseLinksCategories = [];

		[...categoryIds, ...allGluCoursePages].forEach((e) => {
			// eslint-disable-next-line compat/compat
			fetch(`/api/likes?item=${e.id}`)
				.then((res) => res.json())
				// eslint-disable-next-line no-shadow
				.then((courseData) => {
					const obj = {
						id: e.id,
						likes: courseData.likes,
					};
					if (obj.likes > 0) {
						if (
							allCourseArray.some((course) =>
								course.includes(e.id)
							)
						) {
							allCourseCategories.push(obj);
						} else {
							allCourseLinksCategories.push(obj);
						}
					}
				})
				// eslint-disable-next-line no-console
				.catch((err) => console.error(err));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDemandRef = useRef();
	const coursesRef = useRef();
	const webcastRef = useRef(null);

	const scroll = (anchor) => {
		let top;
		switch (anchor) {
			case 'demand':
				top = onDemandRef;
				break;
			case 'courses':
				top = coursesRef;
				break;
			case 'webcast':
				top = webcastRef;
				break;
			default:
				top = null;
				break;
		}
		setTimeout(() => {
			window.scrollTo({
				top: top?.current?.offsetTop - 175,
				left: 0,
				behavior: 'smooth',
			});
		}, 100);
	};

	useEffect(() => {
		const isBrowser = typeof window !== 'undefined';
		if (isBrowser) {
			if (window.location.href.includes('#on-demand')) {
				scroll('demand');
			}
			if (window.location.href.includes('#courses')) {
				scroll('courses');
			}
			if (window.location.href.includes('#webcast')) {
				scroll('webcast');
			}
		}
	});

	const { locale: urlLocale } = data.datoCmsGluClientPage;

	const removeHash = () => {
		if (window.location.hash) {
			window.history.pushState(
				'',
				document.title,
				window.location.pathname + window.location.search
			);
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', removeHash);
		return () => {
			window.removeEventListener('scroll', removeHash);
		};
	}, []);
	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={process.env.GATSBY_RECAPTCHA_KEY}
		>
			<GLUExitPopUp locale={locale} />
			<Head metaContent={metaContent} seoSchemas={seoSchemas} />

			<section className="">
				<GluNewMainHeader
					urlLocale={urlLocale}
					{...headerCTAContent}
					glu
					logo={imageLogo}
					rating={data.datoCmsTrustpilotRating}
				/>

				{locale !== 'en-CA' && courseLinks.length > 0 ? (
					<div ref={coursesRef}>
						<PageSection
							theme="Inherit"
							titleBlock={eLearningCoursesContent.value}
							logo={mainLogo}
							className="max-w-none"
						>
							<div className="relative w-full">
								<div className="w-full overflow-hidden">
									<div
										className="flex transition-transform duration-300"
										style={{
											transform: `translateX(-${
												(currentIndex * 100) / 4
											}%)`,
										}}
									>
										<div className="my-4 md:flex w-full">
											{courseLinks.map((course) => (
												<div className="my-4 glumsdsm:w-[25%] md:flex gluxs:w-full glumd:w-1/4 glumd:gap-0 p-2 glu2xl:w-1/4 flex-shrink-0">
													<Card
														cardContent={course}
														key={course.id}
														logo={mainLogo}
														locale={locale}
													/>
												</div>
											))}
										</div>
									</div>
								</div>

								{courseLinks.length > 4 && (
									<>
										<button
											type="button"
											onClick={handlePrev}
											className={`absolute top-1/2 -left-12 glumd:-left-7 transform -translate-y-1/2 text-white  rounded-full p-2 ${
												currentIndex === 0
													? 'opacity-50 cursor-not-allowed'
													: ''
											} gluxs:hidden glumd:block`}
											disabled={currentIndex === 0}
										>
											<ChevronLeft
												className={clsx(
													'w-7 h-7 text-brand-pale-500 cursor-pointer',
													currentIndex === 0 &&
														'text-grey-400 cursor-not-allowed'
												)}
											/>
										</button>
										<button
											type="button"
											onClick={handleNext}
											className={`absolute top-1/2 -right-12 glumd:-right-7  transform -translate-y-1/2 text-white rounded-full p-2 ${
												currentIndex >=
												courseLinks.length - 4
													? 'opacity-50 cursor-not-allowed'
													: ''
											} gluxs:hidden glumd:block`}
											disabled={
												currentIndex >=
												courseLinks.length - 4
											}
										>
											<ChevronRight
												className={clsx(
													'w-7 h-7 text-brand-pale-500 cursor-pointer',
													currentIndex ===
														courseLinks.length -
															4 &&
														'text-grey-400 cursor-not-allowed'
												)}
											/>
										</button>
									</>
								)}
							</div>
						</PageSection>
					</div>
				) : null}
				<div ref={onDemandRef}>
					<NewGluCardContainer
						cardContainerContent={{
							id: 'course-container',
						}}
						cardVariant="courseList"
						data={onDemandCourses}
						associatedCategory={
							onDemandCourses?.nodes?.[0]?.associatedCategory
						}
						locale={locale}
					/>
				</div>
			</section>
		</GoogleReCaptchaProvider>
	);
};
export default NewGluClientPage;
